import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ============================================
// TYPES
// ============================================

export interface DeleteFilter {
  id: string;
  field: string;
  fieldLabel: string;
  operator: 'equals' | 'notEquals' | 'lessThan' | 'greaterThan' | 'contains' | 'startsWith' | 'isNull' | 'isNotNull';
  value: string | number | boolean | Date;
}

export interface MassDeleteJob {
  id: string;
  jobNumber: string;
  objectName: string;
  objectLabel: string;

  // Deletion criteria
  filters: DeleteFilter[];
  filterLogic: 'AND' | 'OR';

  // Delete options
  deleteType: 'hard' | 'soft';

  // Records
  totalRecords: number;
  deletedRecords: number;
  failedRecords: number;

  // Status
  status: 'preview' | 'pending' | 'in-progress' | 'completed' | 'failed' | 'cancelled';

  // Metadata
  reason?: string;
  createdBy: string;
  createdDate: string;
  startedDate?: string;
  completedDate?: string;

  // Error info
  errorMessage?: string;
  errorDetails?: string;
}

export interface DeletePreview {
  objectName: string;
  totalMatching: number;
  records: Array<{
    id: string;
    name: string;
    [key: string]: any;
  }>;
  warnings: string[];
}

// ============================================
// MOCK DATA
// ============================================

export const mockDeleteJobs: MassDeleteJob[] = [
  {
    id: 'del_001',
    jobNumber: 'DEL-001',
    objectName: 'Client_c',
    objectLabel: 'Client',
    filters: [
      {
        id: 'filter_001',
        field: 'status_c',
        fieldLabel: 'Status',
        operator: 'equals',
        value: 'Inactive'
      },
      {
        id: 'filter_002',
        field: 'last_activity_c',
        fieldLabel: 'Last Activity',
        operator: 'lessThan',
        value: '2024-01-01'
      }
    ],
    filterLogic: 'AND',
    deleteType: 'soft',
    totalRecords: 47,
    deletedRecords: 47,
    failedRecords: 0,
    status: 'completed',
    reason: 'Cleanup of inactive clients',
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    startedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    completedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 5 * 60 * 1000).toISOString()
  },
  {
    id: 'del_002',
    jobNumber: 'DEL-002',
    objectName: 'Dispute_c',
    objectLabel: 'Dispute',
    filters: [
      {
        id: 'filter_003',
        field: 'status_c',
        fieldLabel: 'Status',
        operator: 'equals',
        value: 'Closed'
      }
    ],
    filterLogic: 'AND',
    deleteType: 'hard',
    totalRecords: 123,
    deletedRecords: 123,
    failedRecords: 0,
    status: 'completed',
    reason: 'Archive closed disputes',
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    startedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    completedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000).toISOString()
  },
  {
    id: 'del_003',
    jobNumber: 'DEL-003',
    objectName: 'Client_c',
    objectLabel: 'Client',
    filters: [
      {
        id: 'filter_004',
        field: 'email_c',
        fieldLabel: 'Email',
        operator: 'isNull',
        value: ''
      }
    ],
    filterLogic: 'AND',
    deleteType: 'soft',
    totalRecords: 12,
    deletedRecords: 0,
    failedRecords: 0,
    status: 'in-progress',
    reason: 'Remove records without email',
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    startedDate: new Date(Date.now() - 5 * 60 * 1000).toISOString()
  }
];

// ============================================
// QUERY KEYS
// ============================================

export const massDeleteKeys = {
  all: ['mass-delete'] as const,
  lists: () => [...massDeleteKeys.all, 'list'] as const,
  list: (objectName?: string) => [...massDeleteKeys.lists(), objectName] as const,
  details: () => [...massDeleteKeys.all, 'detail'] as const,
  detail: (id: string) => [...massDeleteKeys.details(), id] as const,
  preview: (objectName: string, filters: DeleteFilter[]) =>
    [...massDeleteKeys.all, 'preview', objectName, filters] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all mass delete jobs
 */
export function useMassDeleteJobs(objectName?: string) {
  return useQuery({
    queryKey: massDeleteKeys.list(objectName),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 400));

      if (objectName) {
        return mockDeleteJobs.filter(job => job.objectName === objectName);
      }

      return mockDeleteJobs;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Get single mass delete job
 */
export function useMassDeleteJob(jobId: string) {
  return useQuery({
    queryKey: massDeleteKeys.detail(jobId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const job = mockDeleteJobs.find(j => j.id === jobId);
      if (!job) throw new Error('Delete job not found');

      return job;
    },
    staleTime: 30 * 1000,
    enabled: !!jobId,
  });
}

/**
 * Preview records that will be deleted
 */
export function useDeletePreview(objectName: string, filters: DeleteFilter[], enabled: boolean = true) {
  return useQuery({
    queryKey: massDeleteKeys.preview(objectName, filters),
    queryFn: async (): Promise<DeletePreview> => {
      await new Promise(resolve => setTimeout(resolve, 600));

      // Simulate matching records
      const totalMatching = Math.floor(Math.random() * 100) + 10;

      const records = Array.from({ length: Math.min(totalMatching, 10) }, (_, i) => ({
        id: `record_${i + 1}`,
        name: `Record ${i + 1}`,
        status: 'Inactive',
        lastActivity: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }));

      const warnings = [];
      if (totalMatching > 1000) {
        warnings.push('Large deletion detected. Consider running in batches.');
      }
      if (filters.length === 0) {
        warnings.push('No filters applied. This will delete ALL records.');
      }

      return {
        objectName,
        totalMatching,
        records,
        warnings
      };
    },
    staleTime: 0, // Always refetch preview
    enabled: enabled && filters.length > 0,
  });
}

/**
 * Create mass delete job
 */
export function useCreateMassDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobData: Omit<MassDeleteJob, 'id' | 'jobNumber' | 'deletedRecords' | 'failedRecords' | 'status' | 'createdBy' | 'createdDate'>) => {
      await new Promise(resolve => setTimeout(resolve, 800));

      const newJob: MassDeleteJob = {
        ...jobData,
        id: `del_${Date.now()}`,
        jobNumber: `DEL-${String(mockDeleteJobs.length + 1).padStart(3, '0')}`,
        deletedRecords: 0,
        failedRecords: 0,
        status: 'pending',
        createdBy: 'Current User',
        createdDate: new Date().toISOString()
      };

      mockDeleteJobs.unshift(newJob);

      // Simulate job processing
      setTimeout(() => {
        const job = mockDeleteJobs.find(j => j.id === newJob.id);
        if (job) {
          job.status = 'in-progress';
          job.startedDate = new Date().toISOString();
          queryClient.invalidateQueries({ queryKey: massDeleteKeys.lists() });
        }
      }, 2000);

      setTimeout(() => {
        const job = mockDeleteJobs.find(j => j.id === newJob.id);
        if (job) {
          job.status = 'completed';
          job.deletedRecords = job.totalRecords;
          job.completedDate = new Date().toISOString();
          queryClient.invalidateQueries({ queryKey: massDeleteKeys.lists() });
        }
      }, 5000);

      return newJob;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: massDeleteKeys.lists() });
    },
  });
}

/**
 * Cancel mass delete job
 */
export function useCancelMassDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      await new Promise(resolve => setTimeout(resolve, 400));

      const job = mockDeleteJobs.find(j => j.id === jobId);
      if (!job) throw new Error('Delete job not found');

      if (job.status !== 'pending' && job.status !== 'in-progress') {
        throw new Error('Cannot cancel completed or failed job');
      }

      job.status = 'cancelled';

      return job;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: massDeleteKeys.lists() });
    },
  });
}

/**
 * Restore soft-deleted records
 */
export function useRestoreDeletedRecords() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      await new Promise(resolve => setTimeout(resolve, 800));

      const job = mockDeleteJobs.find(j => j.id === jobId);
      if (!job) throw new Error('Delete job not found');

      if (job.deleteType !== 'soft') {
        throw new Error('Cannot restore hard-deleted records');
      }

      if (job.status !== 'completed') {
        throw new Error('Can only restore completed deletions');
      }

      return {
        jobId,
        restoredRecords: job.deletedRecords
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: massDeleteKeys.lists() });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get operator label
 */
export function getOperatorLabel(operator: DeleteFilter['operator']): string {
  const labels: Record<DeleteFilter['operator'], string> = {
    equals: 'equals',
    notEquals: 'does not equal',
    lessThan: 'less than',
    greaterThan: 'greater than',
    contains: 'contains',
    startsWith: 'starts with',
    isNull: 'is blank',
    isNotNull: 'is not blank'
  };
  return labels[operator];
}

/**
 * Format filter for display
 */
export function formatFilter(filter: DeleteFilter): string {
  const needsValue = !['isNull', 'isNotNull'].includes(filter.operator);
  const value = needsValue ? ` "${filter.value}"` : '';
  return `${filter.fieldLabel} ${getOperatorLabel(filter.operator)}${value}`;
}

/**
 * Get status color
 */
export function getStatusColor(status: MassDeleteJob['status']): string {
  const colors: Record<MassDeleteJob['status'], string> = {
    preview: 'gray',
    pending: 'yellow',
    'in-progress': 'blue',
    completed: 'green',
    failed: 'red',
    cancelled: 'gray'
  };
  return colors[status];
}

/**
 * Calculate delete duration
 */
export function getDeleteDuration(job: MassDeleteJob): string {
  if (!job.startedDate || !job.completedDate) return 'N/A';

  const duration = new Date(job.completedDate).getTime() - new Date(job.startedDate).getTime();
  const minutes = Math.floor(duration / 60000);
  const seconds = Math.floor((duration % 60000) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}
